(() => {
  'use strict';
  const clean = value => String(value || '').trim().toLowerCase();
  const stoneAllowances = Object.freeze({moissanite:0,lab_grown_diamond:1.5,natural_diamond:5});
  const normalStone = value => Object.hasOwn(stoneAllowances,clean(value)) ? clean(value) : 'moissanite';
  const estimateStonePrice = (variantPrice,baselinePrice,stone) => {
    const price = Number(variantPrice);
    const baseline = Number(baselinePrice);
    if (!Number.isFinite(price) || !Number.isFinite(baseline) || price <= 0 || baseline <= 0) return null;
    return Math.max(500,Math.round((price+baseline*stoneAllowances[normalStone(stone)])/500)*500);
  };
  const formatEstimate = (price,currency,locale) => new Intl.NumberFormat(locale || 'en-GB',{style:'currency',currency:currency || 'GBP'}).format(price/100);
  const normalSize = value => clean(value).replace(/\s+/g,'').replace(/carats?|cts?/g,'ct');
  const displayAlt = value => String(value || '').split('|').map(part => part.trim()).filter(part => !/^drive\s*:/i.test(part)).map(part => part.replace(/^(finish|size)\s*:\s*/i,'')).filter(Boolean).join(' — ');
  const mediaTags = alt => {
    const text = String(alt || '');
    return {finish: clean(text.match(/(?:^|\|)\s*finish\s*:\s*([^|]+)/i)?.[1] || (/silver-tone|white-gold tone/i.test(text) ? 'white' : /rose-gold tone/i.test(text) ? 'rose' : /yellow-gold tone/i.test(text) ? 'yellow' : '')),size: normalSize(text.match(/(?:^|\|)\s*size\s*:\s*([^|]+)/i)?.[1])};
  };
  const selectionFinish = (options, values) => {
    const plating = clean(values[options.findIndex(name => /plating|finish/i.test(name))]);
    const metal = clean(values[options.findIndex(name => /base|metal/i.test(name))]);
    if (/rose|pink/.test(plating)) return 'rose';
    if (/rhodium|white/.test(plating)) return 'white';
    if (/yellow/.test(plating)) return 'yellow';
    return /gold/.test(metal) && !/silver/.test(metal) ? 'yellow' : 'white';
  };
  const selectionSize = (options, values) => normalSize(values[options.findIndex(name => /design size|carat|stone size|size|weight/i.test(name))]);
  const matchesMedia = (alt, finish, size) => {
    const tags = mediaTags(alt);
    const finishTag = /^(silver|rhodium|w)$/.test(tags.finish) ? 'white' : /^(pink|r)$/.test(tags.finish) ? 'rose' : /^(gold|y)$/.test(tags.finish) ? 'yellow' : tags.finish;
    return (!finishTag || finishTag === 'all' || finishTag === finish) && (!tags.size || tags.size === 'all' || tags.size === size);
  };
  const gallerySelection = (items,{filter = false,finish = '',size = '',showAll = false,initialId = ''} = {}) => {
    const matching = filter ? items.filter(item => matchesMedia(item.alt,finish,size)) : items;
    const exact = matching.filter(item => {
      const tags = mediaTags(item.alt);
      return tags.finish && tags.finish !== 'all' && (!size || tags.size === size);
    });
    const videos = items.filter(item => item.type === 'video');
    const selected = matching.length ? items.filter(item => item.type === 'video' || matching.includes(item)) : items;
    const visible = showAll ? items : selected;
    const preferred = showAll ? visible : exact.length ? exact : matching;
    const firstVideo = preferred.find(item => item.type === 'video') || matching.find(item => item.type === 'video') || videos[0];
    const first = firstVideo || (!filter && items.find(item => item.id === initialId)) || preferred.find(item => item.type === 'image') || visible[0];
    return {visible,first,canFilter:selected.length < items.length};
  };
  const enquiryDescription = (title,selections,reference = '',limit = 160) => {
    const name = String(title || '').split(' — ')[0].trim();
    const detail = selections.filter(Boolean).join(' · ');
    const suffix = detail ? ' — ' + detail : '';
    const nameLimit = Math.max(1,limit-suffix.length);
    const shortName = name.length > nameLimit ? name.slice(0,Math.max(1,nameLimit-1)) + '…' : name;
    const description = (shortName + suffix).slice(0,limit);
    return reference && description.length+reference.length+3 <= limit ? description + ' — ' + reference : description;
  };
  const findVariant = (variants, selected) => variants.find(variant => variant.options.length === selected.length && variant.options.every((value,index) => value === selected[index]));
  const compatibleValues = (variants, selected, index) => new Set(variants.filter(variant => variant.options.every((value,at) => at >= index || value === selected[at])).map(variant => variant.options[index]));
  const mount = scope => {
    scope.querySelectorAll('[data-lh-configurator]').forEach(section => {
      if (section.dataset.configured) return;
      section.dataset.configured = 'true';
      let config;
      try { config = JSON.parse(section.querySelector('[data-config-json]').textContent); } catch { return; }
      const pickers = Array.from(section.querySelectorAll('[data-config-option]'));
      const image = section.querySelector('.product-primary-image');
      const imageButton = section.querySelector('[data-image-zoom]');
      const videoStage = section.querySelector('[data-config-video]');
      const thumbnails = Array.from(section.querySelectorAll('[data-config-thumb]'));
      const note = section.querySelector('[data-config-media-note]');
      const allButton = section.querySelector('[data-config-show-all]');
      const status = section.querySelector('[data-config-status]');
      const price = section.querySelector('[data-product-price]');
      const enquiryLink = section.querySelector('.lh-product-actions a[href*="?design="]');
      const stoneInputs = config.stonesEnabled ? Array.from(section.querySelectorAll('[data-config-stone-input]')) : [];
      const selectedStone = () => stoneInputs.find(input => input.checked);
      const initialStone = normalStone(new URL(location.href).searchParams.get('stone'));
      stoneInputs.forEach(input => { input.checked = input.value === initialStone; });
      let showAll = false;
      let currentMedia = null;
      const selected = () => pickers.map(picker => picker.querySelector('input:checked')?.value || '');
      const showMedia = thumb => {
        if (!thumb) return;
        const isVideo = thumb.dataset.configType === 'video';
        const template = isVideo ? section.querySelector(`template[data-config-video-id="${thumb.dataset.configThumb}"]`) : null;
        if (isVideo && (!template || !videoStage)) return;
        if (currentMedia !== thumb || !videoStage?.firstElementChild) {
          videoStage?.querySelectorAll('video').forEach(video => video.pause());
          if (videoStage) { videoStage.replaceChildren(); videoStage.hidden = true; }
          if (imageButton) { imageButton.hidden = isVideo; imageButton.disabled = isVideo; }
          if (isVideo) {
            videoStage.append(template.content.cloneNode(true));
            videoStage.hidden = false;
            const video = videoStage.querySelector('video');
            video?.addEventListener('error',() => {
              const fallback = thumbnails.find(item => !item.hidden && item.dataset.configType === 'image') || thumbnails.find(item => item.dataset.configType === 'image');
              if (currentMedia === thumb && fallback) showMedia(fallback);
            },{once:true});
            document.dispatchEvent(new CustomEvent('lh:video-ready',{detail:{video}}));
          } else if (image && thumb.dataset.configImage) {
            image.src = thumb.dataset.configImage;
            image.removeAttribute('srcset'); image.removeAttribute('sizes');
            image.alt = displayAlt(thumb.dataset.configAlt) || config.title;
          }
        }
        currentMedia = thumb;
        thumbnails.forEach(item => { const active = item === thumb; item.classList.toggle('is-active',active); item.setAttribute('aria-pressed',String(active)); });
        const values = selected();
        if (note) {
          note.hidden = !pickers.length || matchesMedia(thumb.dataset.configAlt,selectionFinish(config.options,values),selectionSize(config.options,values));
          note.textContent = config.labels.mediaMissing;
        }
      };
      const filterMedia = values => {
        const finish = selectionFinish(config.options,values);
        const size = selectionSize(config.options,values);
        const initialId = section.querySelector('[data-config-initial-media]')?.dataset.configInitialMedia;
        const items = thumbnails.map(thumb => ({id:thumb.dataset.configThumb,type:thumb.dataset.configType,alt:thumb.dataset.configAlt,thumb}));
        const gallery = gallerySelection(items,{filter:pickers.length > 0,finish,size,showAll,initialId});
        const visible = new Set(gallery.visible.map(item => item.thumb));
        thumbnails.forEach(thumb => { thumb.hidden = !visible.has(thumb); });
        if (allButton) { allButton.hidden = !gallery.canFilter; allButton.textContent = showAll ? config.labels.showSelected : config.labels.showAll; }
        showMedia(gallery.first?.thumb);
      };
      const update = () => {
        pickers.forEach((picker,index) => {
          const values = selected();
          const allowed = compatibleValues(config.variants,values,index);
          const inputs = Array.from(picker.querySelectorAll('input'));
          inputs.forEach(input => { input.disabled = !allowed.has(input.value); });
          if (!inputs.some(input => input.checked && !input.disabled)) {
            const first = inputs.find(input => !input.disabled); if (first) first.checked = true;
          }
        });
        const values = selected();
        const variant = pickers.length ? findVariant(config.variants,values) : config.variants[0];
        const stone = selectedStone();
        const estimatedPrice = stone && variant ? estimateStonePrice(variant.price,config.baselinePrice,stone.value) : null;
        const formattedPrice = config.pricingPending ? config.labels.pricePending : variant ? estimatedPrice === null ? variant.formattedPrice : formatEstimate(estimatedPrice,config.currency,config.locale) : config.labels.unavailable;
        const selectionLabels = stone ? values.concat(stone.dataset.stoneLabel || stone.value) : values;
        if (price) price.textContent = formattedPrice;
        if (status) status.textContent = variant ? `${selectionLabels.join(' · ')} — ${formattedPrice}` : config.labels.unavailable;
        section.querySelectorAll('[data-stone-property]').forEach(input => { input.value = stone?.dataset.stoneLabel || ''; input.disabled = !stone; });
        section.querySelectorAll('input[name=id]').forEach(input => { input.value = variant?.id || ''; });
        const addButton = section.querySelector('[data-add-button]');
        if (addButton) { addButton.disabled = Boolean(stone) || !variant?.available; addButton.textContent = variant?.available && !stone ? addButton.dataset.availableLabel : addButton.dataset.unavailableLabel; }
        if (variant) {
          const url = new URL(location.href); url.searchParams.set('variant',variant.id);
          if (stone) url.searchParams.set('stone',stone.value); else url.searchParams.delete('stone');
          history.replaceState({},'',url);
          if (enquiryLink) {
            const reference = new URL(location.pathname,location.origin);
            reference.searchParams.set('variant',variant.id);
            if (stone) reference.searchParams.set('stone',stone.value);
            const enquiryUrl = new URL(enquiryLink.href,location.origin);
            enquiryUrl.searchParams.set('design',enquiryDescription(config.title,selectionLabels,reference.toString()));
            enquiryLink.href = enquiryUrl.toString();
          }
        }
        showAll = false; filterMedia(values);
      };
      const initial = config.variants.find(variant => String(variant.id) === String(config.selected)) || config.variants[0];
      if (initial) pickers.forEach((picker,index) => picker.querySelectorAll('input').forEach(input => { input.checked = input.value === initial.options[index]; }));
      section.querySelector('[data-config-options]')?.addEventListener('change',update);
      thumbnails.forEach(thumb => thumb.addEventListener('click',() => showMedia(thumb)));
      section.querySelector('.product-thumbnails')?.addEventListener('keydown',event => {
        const current = event.target.closest('[data-config-thumb]');
        if (!current || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        const visible = thumbnails.filter(thumb => !thumb.hidden); const at = visible.indexOf(current);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length-1 : (at+(event.key === 'ArrowRight'?1:-1)+visible.length)%visible.length;
        if (visible[next]) { event.preventDefault(); visible[next].focus(); visible[next].click(); }
      });
      allButton?.addEventListener('click',() => { showAll = !showAll; filterMedia(selected()); });
      if (pickers.length || stoneInputs.length) update();
      else if (thumbnails.length) filterMedia([]);
      section.closest('.shopify-section')?.addEventListener('shopify:section:unload',() => videoStage?.querySelectorAll('video').forEach(video => video.pause()));
    });
  };
  const api = {normalStone,estimateStonePrice,formatEstimate,gallerySelection,enquiryDescription,mediaTags,selectionFinish,selectionSize,matchesMedia,findVariant,compatibleValues,displayAlt};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    mount(document);
    document.addEventListener('shopify:section:load',event => mount(event.target));
  }
})();

